import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { EndpointBuilder } from '@reduxjs/toolkit/query/endpointDefinitions';
import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/fetchBaseQuery';
import type { Package, PaymentSession, PaymentHistory, PaymentHistoryResponse } from '../../types/billing';
import type { ApiResponse } from '../../types/api';

// Payment API endpoints
export const paymentApiEndpoints = (builder: EndpointBuilder<BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>, never, 'api'>) => ({
  getPackages: builder.query<Package[], void>({
    query: () => '/payments/packages',
    transformResponse: (response: ApiResponse<Package[]>) => {
      const packages = response.data;
      if (packages.length > 0) {
        const premiumIndex = packages.findIndex((pkg: Package) => pkg.name === "1500 Credits");
        if (premiumIndex !== -1) {
          packages[premiumIndex].isPopular = true;
        }
      }
      return packages;
    },
    providesTags: ['Packages']
  }),
  
  purchaseCredits: builder.mutation<
    PaymentSession,
    { packageId: number; customerEmail?: string }
  >({
    query: ({ packageId, customerEmail }) => {
      const baseUrl = 'https://ourphotos.co';
      return {
        url: `/payments/checkout/${packageId}`,
        method: 'POST',
        body: {
          package_id: packageId,
          success_url: `${baseUrl}/dashboard?success_payment=true&session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${baseUrl}/dashboard?payment_canceled=true`,
          customer_email: customerEmail
        }
      };
    },
    transformResponse: (response: ApiResponse<PaymentSession>) => {
      const session = response.data;
      localStorage.setItem('stripe_pending_session_id', session.id);
      localStorage.setItem('pending_payment_session', session.id);
      return session;
    },
    invalidatesTags: ['Packages']
  }),
  
  checkPaymentStatus: builder.query<
    { 
      status: string; 
      payment_status?: string;
      package_id?: number; 
      error?: string 
    },
    string
  >({
    query: (sessionId) => `/payments/sessions/${sessionId}`,
    transformResponse: (response: ApiResponse<{
      status: string;
      payment_status?: string;
      package_id?: number;
      created_at?: string;
      customer_email?: string;
      amount?: number;
      error?: string;
    }>) => {
      if (!response.success) {
        return {
          status: 'failed',
          error: response.message || 'Payment status check failed'
        };
      }
      return response.data;
    }
  }),
  
  getPaymentHistory: builder.query<PaymentHistoryResponse, void>({
    query: () => '/payments/history',
    providesTags: ['Packages']
  }),
}); 